"use client";

import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { Check, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface DgiDocumentReadyCardProps {
  title: string;
  description: string;
  downloadLabel: string;
  dismissLabel: string;
  downloadErrorMessage: string;
  pdfUrl: string;
  downloadFilename: string;
  onDismiss: () => void;
  className?: string;
}

async function downloadPdfToDevice(url: string, filename: string): Promise<void> {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export function DgiDocumentReadyCard({
  title,
  description,
  downloadLabel,
  dismissLabel,
  downloadErrorMessage,
  pdfUrl,
  downloadFilename,
  onDismiss,
  className,
}: DgiDocumentReadyCardProps): JSX.Element {
  const [downloading, setDownloading] = useState(false);

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const handleDownload = useCallback(async (): Promise<void> => {
    if (isMobile) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
      return;
    }
    setDownloading(true);
    try {
      await downloadPdfToDevice(pdfUrl, downloadFilename);
    } catch {
      try {
        window.open(pdfUrl, "_blank", "noopener,noreferrer");
      } catch {
        toast.error(downloadErrorMessage);
      }
    } finally {
      setDownloading(false);
    }
  }, [downloadErrorMessage, downloadFilename, isMobile, pdfUrl]);

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-start gap-4">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-green-600/10 dark:bg-green-500/15"
          aria-hidden
        >
          <Check className="h-6 w-6 text-green-600 dark:text-green-500" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 space-y-1.5">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={downloading}
          onClick={onDismiss}
          className="text-muted-foreground transition-colors duration-150"
        >
          {dismissLabel}
        </Button>
        <Button
          type="button"
          disabled={downloading}
          onClick={() => void handleDownload()}
          className="transition-colors duration-150"
        >
          {downloading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          ) : null}
          {downloadLabel}
        </Button>
      </div>
    </div>
  );
}
